import { FormArray, FormControl, FormGroup } from '@angular/forms'

export enum AllocationLevel {
  ORGANIZATION = 'ORGANIZATION',
  WORKSPACE = 'WORKSPACE',
  ASSET = 'ASSET',
}

export type RoleDefinition = {
  allocationLevel: AllocationLevel
  description: string
  id: string
  name: string
  owningOrganizationId: string
  owningWorkspaceId: string
  systemOwned: boolean
}

export const AllocationLevelsDictionary: Record<AllocationLevel, string | null> = {
  ORGANIZATION: null,
  WORKSPACE: 'Workspace',
  ASSET: 'Project Role',
}

export const AllocationLevelsIcons: Record<AllocationLevel, string | null> = {
  ORGANIZATION: null,
  WORKSPACE: 'ph ph-globe',
  ASSET: 'ph ph-folders',
}

// TODO this needs to be moved to another file and updated when we have the full list
export type PermissionType = 'create' | 'edit' | 'read' | 'delete' | 'share' | 'owner'

// TODO this needs to be updated
export type PermissionValue =
  | 'org_workspaces_create'
  | 'org_workspaces_read'
  | 'org_workspaces_update'
  | 'org_workspaces_delete'
  | 'organization_edit'
  | 'org_workspaces_edit'
  | 'org_users_read'
  | 'org_users_edit'
  | 'org_users_create'
  | 'org_users_delete'
  | 'org_groups_read'
  | 'org_groups_edit'
  | 'org_groups_create'
  | 'org_groups_delete'
  | 'org_roles_read'
  | 'org_roles_edit'
  | 'org_roles_create'
  | 'org_roles_delete'
  | 'wks_font_read'
  | 'wks_font_create'
  | 'wks_font_delete'
  | 'wks_font_edit'
  | 'org_font_read'
  | 'org_font_edit'
  | 'org_font_create'
  | 'org_font_delete'
  | 'workspace_read'
  | 'workspace_edit'
  | 'wks_users_read'
  | 'wks_users_edit'
  | 'wks_users_create'
  | 'wks_users_delete'
  | 'wks_groups_read'
  | 'wks_groups_edit'
  | 'wks_groups_create'
  | 'wks_groups_delete'
  | 'wks_roles_read'
  | 'wks_roles_edit'
  | 'wks_roles_create'
  | 'wks_roles_delete'
  | 'project_edit'
  | 'project_create'
  | 'project_delete'
  | 'workspace_builds_read'
  | 'mediafile_read'
  | 'mediafile_edit'
  | 'mediafile_create'
  | 'mediafile_delete'
  | 'theme_read'
  | 'theme_edit'
  | 'theme_create'
  | 'theme_delete'
  | 'aimodel_read'
  | 'aimodel_edit'
  | 'process_read'
  | 'process_edit'
  | 'enum_read'
  | 'enum_edit'
  | 'projects_admin'
  | 'workspaces_admin'
  | 'wks_audit_log_read'
  | 'org_audit_log_read'
  | 'wks_platform_status_read'
  | 'org_platform_status_read'
  | 'aiagent_edit'
  | 'project_owner'
  | 'org_env_info_read'
  | 'org_env_info_edit'
  | 'wks_env_info_read'
  | 'wks_env_info_edit'
  // Builds
  | 'wks_builds_create'
  | 'wks_builds_read'
  // Active policy
  | 'wks_active_policy_read'
  | 'wks_active_policy_edit'
  // Scheduled processes
  | 'wks_scheduled_processes_read'
  | 'wks_scheduled_processes_edit'
  | 'wks_scheduled_processes_delete'
  //  Configuration parameters overrides
  | 'wks_config_param_overrides_read'
  | 'wks_config_param_overrides_create'
  | 'wks_config_param_overrides_edit'
  | 'wks_config_param_overrides_delete'
  // Task manager
  | 'wks_tasks_read'
  // Runtime content
  | 'wks_runtime_content_edit'
  // Runtime ML models (document classifiers)
  | 'wks_ml_model_content_edit'
  // Out off office
  | 'org_ooo_read'
  | 'org_ooo_edit'
  | 'org_ooo_create'
  | 'org_ooo_delete'
  // Process instance variables edit
  | 'wks_process_instance_variables_edit'
  // Process instance corrective actions (bulk migration, token move)
  | 'wks_corrective_actions_read'
  | 'wks_corrective_actions_create'
  // Saas specific permissions
  | 'org_runtime_projects_read'
  | 'organization_saas_edit'
  // New SaaS runtime permissions
  | 'org_runtime_users_read'
  | 'org_runtime_users_create'
  | 'org_runtime_users_edit'
  | 'org_runtime_users_delete'
  | 'org_runtime_roles_read'
  | 'org_runtime_roles_create'
  | 'org_runtime_roles_edit'
  | 'org_runtime_roles_delete'
  | 'org_runtime_groups_read'
  | 'org_runtime_groups_create'
  | 'org_runtime_groups_delete'
  | 'org_runtime_groups_edit'
  // Usage stats
  | 'org_usage_stats_read'
  // Branding
  | 'org_config_edit'
  | 'org_config_read'
  // AI Providers & Models
  | 'org_ai_providers_read'
  | 'org_ai_providers_edit'
  | 'wks_ai_models_read'
  | 'wks_ai_models_edit'

export type PermissionsSchema = {
  groupKey: Lowercase<AllocationLevel>
  groupName: string
  permissions: {
    key: PermissionValue
    column: PermissionType
  }[]
  availableColumns: PermissionType[] | null
  children?: PermissionsSchema[]
}

export type FlatPermissionsSchema = Record<
  PermissionsSchema['groupKey'],
  PermissionsSchema & { isParent: boolean }
>

export type PermissionsFormArray = FormArray<FormControl<boolean>>
export type PermissionsFormGroup = FormGroup<{
  [key in PermissionsSchema['groupKey']]: PermissionsFormArray
}>

export function flattenPermissionsSchema(
  permissionsSchema: PermissionsSchema[],
  flat?: FlatPermissionsSchema
): FlatPermissionsSchema {
  return permissionsSchema.reduce(
    (acc, group) => {
      acc[group.groupKey] = { ...group, isParent: !group.permissions && group.children?.length > 0 }
      if (group?.children?.length) {
        return flattenPermissionsSchema(group.children, acc)
      }
      return acc
    },
    flat || ({} as FlatPermissionsSchema)
  )
}

// TODO enrich this type with more asset types when needed
export type RoleAssetType = 'project'
